/* eslint-disable complexity */
'use client';

import React, { useEffect, useMemo } from 'react';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import themeSchema, {
  themeToTailwindClassMap as origClassMap,
  getTailwindTextColorForTheme,
} from '../../../lib/composer/schemas/theme';
import { Headers } from './TextBanner';
import { ComposerBlock, PlanSelectorProps } from '../../../types/composer';
import AdminWrapper from '../AdminWrapper';
import { useFetch } from '../../../context/FetchProvider';
import { PlanCardNew } from '../../plans/PlanCardNew';
import { trackPlansView } from '../../plans/utils';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { useComposerBlocksContext } from '../BlockContext';
import { FormattedMessage, useIntl } from 'react-intl';
import { usePlans } from './PlanSelectorHooks';
import headingSchema from '../../../lib/composer/schemas/heading';
import subheadingSchema from '../../../lib/composer/schemas/subheading';
import textSchema from '../../../lib/composer/schemas/text';
import { fontStyleSchema } from '../../../lib/composer/schemas/font';
import overrideBackgroundSchema from '../../../lib/composer/schemas/backgroundColor';
import {
  bg20,
  bg0,
  bgPlanFlexible,
  bgPlanValue,
} from '@osmosis/tailwind-ui/styles/background';
import { text0 } from '@osmosis/tailwind-ui/styles/text';
import { borderPlanPopular } from '@osmosis/tailwind-ui/styles/border';
import {
  getPlanSelectorFields,
  discountPillsSchema,
} from './PlanSelectorSchema';
import { ResolvedPlan } from '../../../types/plans';
import RichText from '../block-ui/RichText';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';

/**
 * Button component for selecting a plan.
 * It displays the plan title based on the keyName and applies styles based on selection.
 */
const PlanSelectorButton = ({
  keyName,
  handleClick,
  selectedKey,
}: {
  keyName: string;

  handleClick: (key: string) => void;
  selectedKey: string;
}) => {
  const { formatMessage } = useIntl();
  const buttonTitle = (key?: string) => {
    if (!key) return undefined;
    const map = [
      {
        test: (k: string) => k.includes('bassci'),
        label: formatMessage({
          id: 'components.MatrixPlanSelector.basicsciences',
          defaultMessage: 'Basic Sciences',
        }),
      },
      {
        test: (k: string) => k.includes('suite'),
        label: formatMessage({
          id: 'components.MatrixPlanSelector.suite',
          defaultMessage: 'Osmosis Suite',
        }),
      },
    ];
    const found = map.find(({ test }) => test(key));
    return found?.label;
  };

  // Check if this button is the active one

  const isActive = selectedKey === keyName;

  return (
    <Button
      onClick={() => handleClick(keyName)}
      data-testid={`product-button-${keyName}`}
      rounded={true}
      color="transparent"
      className={`
        ${
          isActive
            ? 'border-2 border-[#134986] bg-white text-[#134986]'
            : 'bg-[#ebebeb] text-gray-600 '
        }
        transition-colors
      `}
      aria-pressed={isActive}
    >
      {buttonTitle(keyName)}
    </Button>
  );
};

/**
 * Plan Selector component that displays different plan options.
 */
const PlanSelectorNew = (props: PlanSelectorProps) => {
  const {
    heading,
    subheading,
    text,
    theme = '',
    plansPage,
    program = '', // Don't set a default program, require explicit selection
    productID = '', // Empty productID means "Program Default"
    planLengths = ['6', '12', '24'], // Default plan lengths as strings
    planVersion = '', // Plan version for program1
    program2 = '', // Explicitly set program2 to empty string as default
    productID2 = '', // Empty productID2 means "Program Default"
    planLengths2,
    planVersion2 = '',
    dealType,
    dealCode,
    dealCodes,
    discountPills,
    fontStyle,
    overrideBackground,
  } = props;
  const { formatMessage } = useIntl();

  const [selectedKey, setSelectedKey] = React.useState<string>('');

  const themeClassMap: { [key: string]: string } = {
    ...origClassMap,
    white: `${bg0}`,
    transparent: 'bg-transparent',
  };

  const headerProps = { heading, theme, subheading };
  const pageDeal = plansPage?.deal;
  const tieredDealCodes = useMemo(
    () =>
      (pageDeal?.tieredDiscounts || []).flatMap((tier) =>
        tier.dealCode
          ? [{ planLength: tier.planLength, dealCode: tier.dealCode }]
          : [],
      ),
    [pageDeal],
  );

  const fetch = useFetch();

  const context = (useComposerBlocksContext() || {}) as {
    setDisabledColumns: (index: boolean) => void;
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const { setDisabledColumns = () => {} } = context;

  // Use useMemo to memoize processed props to prevent unnecessary re-renders
  const processedProps = useMemo(
    () => ({
      program,
      productID: typeof productID === 'number' ? String(productID) : productID,
      planLengths: Array.isArray(planLengths)
        ? planLengths.map((length) => String(length))
        : ['6', '12', '24'],
      planVersion: String(planVersion || ''), // Ensure planVersion is a string
      program2,
      productID2:
        typeof productID2 === 'number' ? String(productID2) : productID2,
      planLengths2: Array.isArray(planLengths2)
        ? planLengths2.map((length) => String(length))
        : undefined,
      planVersion2: String(planVersion2 || ''), // Ensure planVersion2 is a string
      dealType,
      dealCode: pageDeal?.url || dealCode,
      dealCodes: tieredDealCodes.length ? tieredDealCodes : dealCodes,
      fetch,
    }),
    [
      program,
      productID,
      planLengths,
      planVersion,
      program2,
      productID2,
      planLengths2,
      planVersion2,
      dealType,
      pageDeal?.url,
      dealCode,
      tieredDealCodes,
      dealCodes,
      fetch,
    ],
  );

  // Use memoized props to avoid loops
  const { plans, plansByCode, keys, setPlans, suitePlans } =
    usePlans(processedProps);

  // Update selectedKey when keys change
  useEffect(() => {
    if (Array.isArray(keys) && keys.length > 0) {
      setSelectedKey(keys[0]);
    }
  }, [keys]);

  const isMultiPackage =
    keys.length === 2 &&
    keys.every(
      (key) => key && (key.includes('bassci') || key.includes('suite')),
    ) &&
    keys.every((key) => (plansByCode[key] || []).length > 0);

  // On initial load, set based on default selected key
  useEffect(() => {
    if (Array.isArray(keys) && keys.length > 0 && keys[0]) {
      setDisabledColumns(keys[0].includes('bassci'));
    }
  }, [keys, setDisabledColumns]);

  useEffect(() => {
    trackPlansView(plans);
  }, [plans]);

  // Show guidance message if no program is selected
  if (!program) {
    return (
      <AdminWrapper>
        <Box className="rounded border border-dashed border-gray-300 p-5 text-center">
          <FormattedMessage
            id="planSelector.selectProgram"
            defaultMessage="Please select a program in the editor to display plans"
          />
        </Box>
      </AdminWrapper>
    );
  }

  const handleClick = (selectedCode: string) => {
    setSelectedKey(selectedCode);
    const selectedPlans = plansByCode[selectedCode] || [];
    setPlans(selectedPlans);

    // Example: fade if key includes 'bassci'
    setDisabledColumns(selectedCode.includes('bassci'));
  };

  // Determine which plans to show
  let displayedPlans: ResolvedPlan[] = [];
  if (isMultiPackage) {
    displayedPlans = (plansByCode[selectedKey] || []).slice(0, 3);
  } else {
    displayedPlans = plans.slice(0, 3);
  }
  return (
    <AdminWrapper withIntl={false}>
      <Box
        className={cn(
          overrideBackground !== 'default'
            ? overrideBackground
            : themeClassMap[theme] || 'bg-0',
          fontStyle !== 'default' ? fontStyle : '',
        )}
      >
        <Box className="p-3">
          <Flex className="flex flex-1 flex-col items-center">
            <Headers {...headerProps} />
            <Flex
              className="w-full flex-col items-center justify-center pb-10"
              id="outer-wrapper"
            >
              {isMultiPackage && (
                <Box className="my-8">
                  <Flex className="justify-center">
                    <Flex className={`justify-center rounded-[40px] ${bg20}`}>
                      <PlanSelectorButton
                        keyName={keys[0]}
                        handleClick={handleClick}
                        selectedKey={selectedKey}
                      />
                      <PlanSelectorButton
                        keyName={keys[1]}
                        handleClick={handleClick}
                        selectedKey={selectedKey}
                      />
                    </Flex>
                  </Flex>
                </Box>
              )}
              <Flex
                className={cn(
                  'max-w-wp-plans mx-2 flex flex-col items-center justify-center gap-6 md:grid md:items-stretch',
                  {
                    1: 'md:grid-cols-1',
                    2: 'md:grid-cols-2',
                    3: 'md:grid-cols-3',
                  }[Math.min(displayedPlans.length, 3)],
                )}
                data-testid="plans"
              >
                {displayedPlans
                  ?.slice()
                  .sort((a, b) => a.intervalLength - b.intervalLength) // sort by plan length, smallest to largest
                  .map((plan, index) => {
                    const badge =
                      plan.intervalLength === 12
                        ? {
                            text: formatMessage({
                              id: 'plan.most_popular',
                              defaultMessage: 'Most Popular',
                            }),
                            bg: bgPlanFlexible,
                          }
                        : plan.intervalLength === 24
                          ? {
                              text: formatMessage({
                                id: 'plan.best_value',
                                defaultMessage: 'Best Value',
                              }),
                              bg: bgPlanValue,
                            }
                          : undefined;
                    const suitePlan = suitePlans?.find(
                      (p) => p.intervalLength === plan.intervalLength,
                    );
                    return (
                      <Box
                        className={cn(
                          'flex min-h-[355px] w-[245px] flex-col items-center justify-center rounded-lg border border-neutral-200',
                          plan.intervalLength === 12 &&
                            `border-2 ${borderPlanPopular} md:-my-1 md:min-h-[371px]`,
                        )}
                        key={`plan-${plan.code}`}
                        data-testid={`plan-card-${index}`}
                      >
                        <Box
                          className={cn(
                            'z-20 flex min-h-8 w-full flex-col items-center rounded-t-[7px] p-2 text-center text-xs font-bold uppercase',
                            // Plans without a badge keep the header height so every
                            // card title stays aligned, but render as bare card
                            // background - no label, divider or shadow.
                            badge
                              ? cn(
                                  text0,
                                  badge.bg,
                                  'shadow-[0_2px_8px_rgba(0,0,0,0.04)]',
                                )
                              : 'bg-wp-light-blue',
                          )}
                          aria-hidden={!badge}
                          data-testid="plan-badge"
                        >
                          {badge?.text}
                        </Box>
                        <PlanCardNew
                          upsellPlan={suitePlan}
                          plan={plan}
                          dealCode={plan.appliedDealCode}
                          deal={
                            pageDeal?.url === plan.appliedDealCode
                              ? pageDeal
                              : undefined
                          }
                          discountPill={
                            discountPills?.find(
                              ({ planLength }) =>
                                String(planLength) ===
                                String(plan.intervalLength),
                            )?.text
                          }
                        />
                      </Box>
                    );
                  })}
              </Flex>
            </Flex>

            {!!text && (
              <RichText
                theme={theme}
                html={text}
                alignment="center"
                containerProps={{
                  className: `!mt-8 text-center ${getTailwindTextColorForTheme(
                    theme,
                  )}`,
                }}
              />
            )}
          </Flex>
        </Box>
      </Box>
    </AdminWrapper>
  );
};

PlanSelectorNew.schema = {
  name: 'plan-selector-new',
  label: 'Plan Selector New',
  editableProps: [
    ...getPlanSelectorFields(), // set 1 (no suffix)
    ...getPlanSelectorFields('2'), // set 2 (with suffix '2')
    discountPillsSchema,
    headingSchema,
    subheadingSchema,
    textSchema,
    themeSchema,
    fontStyleSchema,
    overrideBackgroundSchema,
  ],
};

/**
 * Returns the program and planLengths arguments from a ComposerBlock.
 * @param {ComposerBlock} block - The ComposerBlock to extract arguments from.
 * @returns {Object} An object containing the program and planLengths arguments.
 */
PlanSelectorNew.getPlansParams = (block: ComposerBlock) => {
  const { productID, planLengths = [6, 12, 24] } = block.args;
  return { productIDs: planLengths.map(() => productID) };
};

export default PlanSelectorNew;
